// MdpIntegration.cshtml (not in the menu, as in the legacy console): what is configured for MDP, a live probe, and
// the planned mapping of MDP's gateway operations (static reference text).
import { dateTimeUtc } from '@qor/web-shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { adminApi, type ProbeResult } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';

const dt = {
  color: 'var(--qor-muted)',
  fontSize: '.78rem',
  fontWeight: 800,
  textTransform: 'uppercase',
  marginBottom: 8,
} as const;
const hint = { marginTop: 8, fontSize: '.85rem', color: 'var(--qor-muted)' } as const;

/** "MMM d, yyyy h:mm:ss tt UTC" */
export function checkedAt(iso: string): string {
  const d = new Date(iso);
  const base = dateTimeUtc(iso) ?? '';
  return base.replace(
    /(\d+):(\d\d) ([AP]M)$/,
    (_m, h: string, mm: string, ampm: string) => `${h}:${mm}:${String(d.getUTCSeconds()).padStart(2, '0')} ${ampm} UTC`,
  );
}

export function MdpIntegrationPage() {
  usePageTitle('MDP Integration');
  const status = useQuery({ queryKey: ['mdp', 'status'], queryFn: adminApi.mdpStatus });
  const [probe, setProbe] = useState<{ result: ProbeResult | null } | null>(null);
  const [busy, setBusy] = useState(false);

  async function runProbe() {
    setBusy(true);
    const result = await adminApi.probe('mdp');
    setBusy(false);
    setProbe({ result });
  }

  const s = status.data;
  const p = probe?.result ?? null;
  return (
    <>
      <div className="qc-page-heading">
        <div>
          <h1>MDP Integration</h1>
          <p className="qc-breadcrumb">
            QOR Console <span aria-hidden="true">&rsaquo;</span> <strong>Provider Gateways</strong>{' '}
            <span aria-hidden="true">&rsaquo;</span> MDP
          </p>
        </div>
        <span className="qc-badge qc-badge-muted">Sandbox access pending</span>
      </div>

      <section className="qc-panel" style={{ marginBottom: 24 }}>
        <div className="qc-panel-title">
          <h3>Connection readiness</h3>
          <span>What&apos;s configured on our side today</span>
        </div>
        {s && (
          <dl style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', margin: 0 }}>
            <div>
              <dt style={dt}>Sandbox base URL</dt>
              <dd style={{ margin: 0 }}>
                <span className={`qc-badge ${s.baseUrlConfigured ? 'qc-badge-success' : 'qc-badge-muted'}`}>
                  {s.baseUrlConfigured ? 'Configured' : 'Missing'}
                </span>
                <div style={hint}>{s.baseUrl}</div>
              </dd>
            </div>
            <div>
              <dt style={dt}>Institution ID (instId)</dt>
              <dd style={{ margin: 0 }}>
                <span className={`qc-badge ${s.instIdConfigured ? 'qc-badge-success' : 'qc-badge-muted'}`}>
                  {s.instIdConfigured ? 'Configured' : 'Awaiting Modupay'}
                </span>
                <div style={hint}>Required on nearly every endpoint</div>
              </dd>
            </div>
            <div>
              <dt style={dt}>Access token</dt>
              <dd style={{ margin: 0 }}>
                <span className={`qc-badge ${s.tokenConfigured ? 'qc-badge-success' : 'qc-badge-muted'}`}>
                  {s.tokenConfigured ? 'Present' : 'Missing'}
                </span>
                {s.tokenConfigured && (
                  <div style={hint}>
                    Expires {s.tokenExpiresAtUtc} &mdash; interim only, pending durable credentials
                  </div>
                )}
              </dd>
            </div>
          </dl>
        )}
      </section>

      <section className="qc-panel" style={{ marginBottom: 24 }}>
        <div className="qc-panel-title">
          <h3>Connectivity probe</h3>
          <span>Live reachability check against the MDP sandbox host</span>
        </div>
        <button type="button" className="qor-button qor-button-primary" disabled={busy} onClick={() => void runProbe()}>
          Run Connectivity Probe
        </button>
        {probe && p && (
          <div
            className={`qc-account-message ${p.reachable ? 'qc-account-message-ok' : ''}`}
            style={{ marginTop: 20 }}
            role="status"
          >
            <strong style={{ display: 'block', marginBottom: 6 }}>
              {p.reachable ? 'Reachable' : 'Not reachable'} &mdash; {p.category}
            </strong>
            {p.message}
            <div style={{ marginTop: 10, fontSize: '.82rem', color: 'var(--qor-muted)' }}>
              Target: <code>{p.target}</code> &middot;{' '}
              {p.httpStatusCode !== null && <>HTTP {p.httpStatusCode} &middot; </>}
              {p.elapsedMs} ms &middot; Checked {checkedAt(p.checkedAtUtc)}
            </div>
          </div>
        )}
        {probe && !p && (
          <div className="qc-account-message" style={{ marginTop: 20 }} role="status">
            Probe did not return a result. Api may be unreachable.
          </div>
        )}
      </section>

      <section className="qc-panel">
        <div className="qc-panel-title">
          <h3>Planned gateway operations</h3>
          <span>Mapped from Modupay&apos;s API Gateway spec (v2.1.8) to our provider gateway contract</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <details style={{ border: '1px solid var(--qor-line)', borderRadius: '8px' }}>
            <summary
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                padding: '12px 14px',
                cursor: 'pointer',
                listStyle: 'none',
              }}
            >
              <span>
                <strong>Authenticate</strong>{' '}
                <span style={{ color: 'var(--qor-muted)', fontWeight: '400' }}>
                  &mdash; GET /authentication-service/{'{instId}'}/auth
                </span>
              </span>
              <span className="qc-badge qc-badge-muted">Mapped</span>
            </summary>
            <div style={{ borderTop: '1px solid var(--qor-line)', padding: '14px' }}>
              <p style={{ margin: '0 0 10px', color: 'var(--qor-muted)', fontSize: '.85rem' }}>
                Internal only &mdash; not a public gateway method. Mirrors the same pattern already built for ZumRails.
              </p>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '.85rem' }}>
                <tbody>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      MDP field
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      &rarr;
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      Our side
                    </th>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>Authorization header (Basic user:pass)</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>
                      Sent by <code>AuthorizeAsync()</code>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>access_token (response)</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>Cached bearer token, reused on subsequent calls</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>

          <details style={{ border: '1px solid var(--qor-line)', borderRadius: '8px' }}>
            <summary
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                padding: '12px 14px',
                cursor: 'pointer',
                listStyle: 'none',
              }}
            >
              <span>
                <strong>Get cardholder / card details</strong>{' '}
                <span style={{ color: 'var(--qor-muted)', fontWeight: '400' }}>
                  &mdash; GET .../{'{customerNumber}'}/cards/{'{cardId}'}
                </span>
              </span>
              <span className="qc-badge qc-badge-muted">Mapped</span>
            </summary>
            <div style={{ borderTop: '1px solid var(--qor-line)', padding: '14px' }}>
              <p style={{ margin: '0 0 10px', color: 'var(--qor-muted)', fontSize: '.85rem' }}>
                <code>GetCardholderProfileAsync</code> + <code>GetCardAccountSnapshotAsync</code>
              </p>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '.85rem' }}>
                <tbody>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      MDP field
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      &rarr;
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      Our field
                    </th>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>personName</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>ProviderCardholderProfile.FirstName / LastName</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>cardMask</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>CardAccountSnapshot.MaskedCardNumber</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>statusCode (numeric, 0&ndash;25+)</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>
                      CardAccountSnapshot.Status &mdash; via a new MDP-specific numeric map, separate from ZumRails'
                      string map
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>avalBalance</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>CardAccountSnapshot.AvailableBalance</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>currency.code</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>CardAccountSnapshot.Currency</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>cardNumber (full PAN, PCI-gated)</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>
                      <strong>Never requested</strong> &mdash; masked form only, per our no-PAN rule
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>

          <details style={{ border: '1px solid var(--qor-line)', borderRadius: '8px' }}>
            <summary
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                padding: '12px 14px',
                cursor: 'pointer',
                listStyle: 'none',
              }}
            >
              <span>
                <strong>Get balance</strong>{' '}
                <span style={{ color: 'var(--qor-muted)', fontWeight: '400' }}>&mdash; POST .../cards/balance</span>
              </span>
              <span className="qc-badge qc-badge-muted">Mapped</span>
            </summary>
            <div style={{ borderTop: '1px solid var(--qor-line)', padding: '14px' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '.85rem' }}>
                <tbody>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      MDP field
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      &rarr;
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      Our field
                    </th>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>balance</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>CardAccountSnapshot.AvailableBalance</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>currencyCode / currencyName</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>CardAccountSnapshot.Currency</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>

          <details style={{ border: '1px solid var(--qor-line)', borderRadius: '8px' }}>
            <summary
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                padding: '12px 14px',
                cursor: 'pointer',
                listStyle: 'none',
              }}
            >
              <span>
                <strong>Get transactions</strong>{' '}
                <span style={{ color: 'var(--qor-muted)', fontWeight: '400' }}>
                  &mdash; GET .../cards/{'{cardId}'}/transactions
                </span>
              </span>
              <span className="qc-badge qc-badge-muted">Mapped</span>
            </summary>
            <div style={{ borderTop: '1px solid var(--qor-line)', padding: '14px' }}>
              <p style={{ margin: '0 0 10px', color: 'var(--qor-muted)', fontSize: '.85rem' }}>
                <code>GetTransactionsAsync</code> &mdash; each item maps to a <code>ProviderTransactionSummary</code>,
                same shape already used for ZumRails.
              </p>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '.85rem' }}>
                <tbody>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      MDP field
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      &rarr;
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      Our field
                    </th>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>transaction amount / currency</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>ProviderTransactionSummary.Amount / Currency</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>transaction date</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>ProviderTransactionSummary.PostedAt</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>merchantName / description</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>ProviderTransactionSummary.Description</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>

          <details style={{ border: '1px solid var(--qor-line)', borderRadius: '8px' }}>
            <summary
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                padding: '12px 14px',
                cursor: 'pointer',
                listStyle: 'none',
              }}
            >
              <span>
                <strong>Activate / unblock card</strong>{' '}
                <span style={{ color: 'var(--qor-muted)', fontWeight: '400' }}>
                  &mdash; PUT .../cards/management/{'{cardId}'}/activate
                </span>
              </span>
              <span className="qc-badge qc-badge-muted">Mapped</span>
            </summary>
            <div style={{ borderTop: '1px solid var(--qor-line)', padding: '14px' }}>
              <p style={{ margin: '0 0 10px', color: 'var(--qor-muted)', fontSize: '.85rem' }}>
                <code>ActivateCardAsync</code> &mdash; same action already built and working for ZumRails.
              </p>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '.85rem' }}>
                <tbody>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      MDP field
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      &rarr;
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      Our field
                    </th>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>status ("1" = successful)</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>ProviderActionResult.Success</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>

          <details style={{ border: '1px solid var(--qor-line)', borderRadius: '8px' }}>
            <summary
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                padding: '12px 14px',
                cursor: 'pointer',
                listStyle: 'none',
              }}
            >
              <span>
                <strong>Freeze (report lost/stolen)</strong>{' '}
                <span style={{ color: 'var(--qor-muted)', fontWeight: '400' }}>
                  &mdash; PUT .../cards/management/{'{cardId}'}/stolen-lost
                </span>
              </span>
              <span className="qc-badge qc-badge-muted">Mapped</span>
            </summary>
            <div style={{ borderTop: '1px solid var(--qor-line)', padding: '14px' }}>
              <p style={{ margin: '0 0 10px', color: 'var(--qor-muted)', fontSize: '.85rem' }}>
                <code>UpdateCardStatusAsync</code> &mdash; reversible via Activate, same "Freeze / Unfreeze" pattern
                already live for ZumRails.
              </p>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '.85rem' }}>
                <tbody>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      MDP field
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      &rarr;
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      Our field
                    </th>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>status ("1" = successful, card set to numeric status 7)</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>ProviderActionResult.Success</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>

          <details style={{ border: '1px solid var(--qor-line)', borderRadius: '8px' }}>
            <summary
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                padding: '12px 14px',
                cursor: 'pointer',
                listStyle: 'none',
              }}
            >
              <span>
                <strong>Card issuance + status polling</strong>{' '}
                <span style={{ color: 'var(--qor-muted)', fontWeight: '400' }}>
                  &mdash; POST .../customers/issue/new-customer + Get Application Status
                </span>
              </span>
              <span className="qc-badge qc-badge-muted">Mapped</span>
            </summary>
            <div style={{ borderTop: '1px solid var(--qor-line)', padding: '14px' }}>
              <p style={{ margin: '0 0 10px', color: 'var(--qor-muted)', fontSize: '.85rem' }}>
                MDP issuance is asynchronous &mdash; it fits our existing <code>OnboardingApplication</code> state
                machine better than it fits ZumRails' simpler hosted-iframe flow.
              </p>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '.85rem' }}>
                <tbody>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      MDP field
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      &rarr;
                    </th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--qor-muted)', fontWeight: '700' }}>
                      Our field
                    </th>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>applicationId</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>OnboardingApplication.Id (external reference)</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>applicationStatus (APST0006 / 0007 / 0008)</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>
                      OnboardingApplication.Status (InProgress / Approved / Declined)
                    </td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 8px' }}>cardId, cardNumber, accountList (on success)</td>
                    <td style={{ padding: '4px 8px' }}>&rarr;</td>
                    <td style={{ padding: '4px 8px' }}>ProviderSubject + CardAccount, created once approved</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>
        </div>
      </section>
    </>
  );
}
